import { HoverCard, LoadingOverlay, Text } from "@mantine/core"
import { useEffect, useState } from "react"
import { useLocation, useParams } from "react-router-dom"
import { CollectionQuery, Order } from "../../../models/collection.model"
import {
  EntityConfig,
  entityViewMode,
} from "../../../models/entity-config.model"
import { User } from "../../../models/user.model"
import EntityList from "../../../shared/component/entity-list/entity-list.component"
import { useLazyGetUsersQuery } from "../store/user.query"
import StatCard from "../../dashboard/component/stat-cart"
import { AlertTriangle, DollarSign, Package, TrendingUp } from "lucide-react"
import { motion } from "framer-motion"
import { t } from "i18next"
import { useTranslation } from "react-i18next"
import { dateFormat } from "../../../shared/utility/date-format/date-format"
export default function UserPage() {
  const params = useParams()
  const { t } = useTranslation()
  const location = useLocation()
  const [viewMode, setViewMode] = useState<entityViewMode>("list")
  const [collection, setCollection] = useState<CollectionQuery>({
    skip: 0,
    top: 10,
    orderBy: [{ field: "createdAt", direction: "desc" }],
  })

  const [getUsers, users] = useLazyGetUsersQuery()

  useEffect(() => {
    getUsers(collection)
  }, [collection])

  useEffect(() => {
    if (params?.id !== undefined) {
      setViewMode("detail")
    } else {
      setViewMode("list")
    }
    if (location.pathname === "/user/archived") {
      setViewMode("detail")
    }
  }, [setViewMode, params?.id, location])

  const data: User[] | undefined = users.data?.data

  const config: EntityConfig<User> = {
    primaryColumn: { key: "name", name: t("name") },
    detailUrl: "detail",
    rootUrl: "/user",
    identity: "id",
    name: "name",
    visibleColumn: [
      {
        key: "name",
        name: t("name"),
        render: (user) => {
          return (
            <Text size="sm" className={user?.deletedAt ? "text-danger" : ""}>
              {user.name}
            </Text>
          )
        },
      },
      { key: "email", name: t("email") },
      { key: "phoneNumber", name: t("phoneNumber") },
      {
        key: "gender",
        name: t("gender"),
        render: (value) => {
          return value.gender.toLowerCase() === "male" ? t("male") : t("female")
        },
      },
      {
        key: "archiveReason",
        name: t("archiveReason"),
        hideSort: true,
        render: (user: User) => {
          if (user.archiveReason) {
            return (
              <HoverCard width={280} shadow="md">
                <HoverCard.Target>
                  <Text size="sm" className={"cursor-pointer"}>
                    {user?.archiveReason && user?.archiveReason?.length > 25
                      ? user?.archiveReason?.substring(0, 22) + "..."
                      : user?.archiveReason}
                  </Text>
                </HoverCard.Target>
                <HoverCard.Dropdown
                  className={
                    "text-justify break-all wrap max-h-60 overflow-auto"
                  }
                >
                  <Text size="sm">{user.archiveReason}</Text>
                </HoverCard.Dropdown>
              </HoverCard>
            )
          } else {
            return t("notArchived")
          }
        },
      },
      {
        key: "createdAt",
        name: t("registeredAt"),
        render: (value: User) => {
          return dateFormat(value?.createdAt, {
            day: true,
            weekday: true,
            hour: false,
            minute: false,
          })
        },
      },
      {
        key: "enabled",
        name: t("status"),
        render(user) {
          return user?.enabled ? t("active") : t("inactive")
        },
      },
    ],
  }

  return (
    <main className="max-w-7xl mx-auto py-6 px-4 lg:px-8 mt-16">
      <LoadingOverlay visible={users.isFetching} />

      {/* <motion.div
        className='grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8'
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
      >

        <StatCard name='Total Products' icon={Package} value='$12,345' color='#6366F1' />
        <StatCard name='Top Selling' icon={TrendingUp} value='1,234' color='#8B5CF6' />
        <StatCard name='Low Stock' icon={AlertTriangle} value='567' color='#EC4899' />
        <StatCard name='Total Revenue' icon={DollarSign} value='12.5%' color='#10B981' />

      </motion.div> */}
      <EntityList
        viewMode={viewMode}
        // showNewButton={false}
        title={t("user", { count: 2 })}
        total={users?.data?.count}
        itemsLoading={users.isFetching}
        config={config}
        items={data}
        initialPage={1}
        defaultPageSize={10}
        pageSize={[10, 15, 20]}
        collectionQuery={collection}
        onArchivedChecked={(e) =>
          setCollection({
            ...collection,
            withArchived: e,
          })
        }
        onPaginationChange={(skip: number, top: number) => {
          if (collection.skip !== skip || collection.top !== top) {
            setCollection({ ...collection, skip: skip, top: top })
          }
        }}
        onSearch={(data: any) => {
          setCollection({
            ...collection,
            search: data,
            searchFrom: ["name", "email", "phoneNumber"],
          })
        }}
        onFilterChange={(data: any) => {
          if (collection?.filter || data.length > 0) {
            setCollection({ ...collection, filter: data })
          }
        }}
        onOrder={(data: Order) =>
          setCollection({ ...collection, orderBy: [data] })
        }
      />
    </main>
  )
}
